import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { DialogContent, DialogTitle } from '@mui/material';
import { QcsDialog } from '../common/basic/QcsDialog';
import { QcsDialogActions } from '../common/basic/QcsDialogActions';
import { QcsDialogContentText } from '../common/basic/QcsDialogContentText';
import { QcsButton } from '../common/basic/QcsButton';

interface Props {
  //The status to change to; null keeps the dialog closed.
  active: boolean | null;
  hasUnsavedChanges: boolean;
  onClose: () => void;
  onConfirm: (saveChanges: boolean) => void;
}

/** Confirms deactivation and, when the settings form has unsaved changes, asks whether to save or discard them first. */
export const IntegrationSetActiveDialog: FC<Props> = ({ active, hasUnsavedChanges, onClose, onConfirm }) => {
  const { t } = useTranslation();

  const handleConfirm = (saveChanges: boolean) => {
    onClose();
    onConfirm(saveChanges);
  };

  const renderActions = () => {
    if (!hasUnsavedChanges) {
      return <QcsButton onClick={() => handleConfirm(false)}>{t('integration.apiAccess.deactivate')}</QcsButton>;
    }
    return (
      <>
        <QcsButton onClick={() => handleConfirm(false)}>
          {t(active ? 'integration.apiAccess.unsavedChanges.discardAndActivate' : 'integration.apiAccess.unsavedChanges.discardAndDeactivate')}
        </QcsButton>
        <QcsButton onClick={() => handleConfirm(true)}>
          {t(active ? 'integration.apiAccess.unsavedChanges.saveAndActivate' : 'integration.apiAccess.unsavedChanges.saveAndDeactivate')}
        </QcsButton>
      </>
    );
  };

  return (
    <QcsDialog open={active !== null} onClose={onClose}>
      <DialogTitle>{t(active ? 'integration.apiAccess.unsavedChanges.title' : 'integration.apiAccess.deactivateTitle')}</DialogTitle>
      <DialogContent>
        {!active && <QcsDialogContentText>{t('integration.apiAccess.deactivateConfirm')}</QcsDialogContentText>}
        {hasUnsavedChanges && <QcsDialogContentText>{t('integration.apiAccess.unsavedChanges.message')}</QcsDialogContentText>}
      </DialogContent>
      <QcsDialogActions>
        <QcsButton onClick={onClose}>{t('common.cancel')}</QcsButton>
        {renderActions()}
      </QcsDialogActions>
    </QcsDialog>
  );
};
